import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { cookieZustandStorage, setAuthCookie } from "./storage";
import { identifyUser } from "@/observability/identify-user";
import { Role } from "@/lib/constants";
import {
  type UserDetailDTO,
  type WorkspaceAccess,
  type WorkspaceKey,
} from "@membentry/shared-types";

const OWNER_ROLE = "Owner";
const SUPERADMIN_ROLE = "SuperAdmin";

const deriveWorkspaceAccess = (
  roles: readonly string[] | null | undefined,
): WorkspaceAccess => {
  const roleSet = new Set(roles ?? []);
  const available: WorkspaceKey[] = [];
  const canAccessOwnerWorkspace = roleSet.has(OWNER_ROLE);
  const canAccessSuperadminWorkspace = roleSet.has(SUPERADMIN_ROLE);

  if (canAccessOwnerWorkspace) {
    available.push("owner");
  }

  if (canAccessSuperadminWorkspace) {
    available.push("superadmin");
  }

  return {
    available,
    defaultWorkspace: available.length === 1 ? available[0] : null,
    canAccessOwnerWorkspace,
    canAccessSuperadminWorkspace,
  };
};

const normalizeUserWorkspaceAccess = <
  T extends { roles: string[]; workspaceAccess?: WorkspaceAccess },
>(
  user: T,
): T & { workspaceAccess: WorkspaceAccess } => ({
  ...user,
  workspaceAccess: user.workspaceAccess ?? deriveWorkspaceAccess(user.roles),
});

const getUserWorkspaceAccess = (
  user:
    | { roles: readonly string[]; workspaceAccess?: WorkspaceAccess }
    | null
    | undefined,
): WorkspaceAccess => {
  if (!user) {
    return deriveWorkspaceAccess([]);
  }

  return user.workspaceAccess ?? deriveWorkspaceAccess(user.roles);
};

export interface UserData {
  id: string;
  email: string;
  role: "admin" | "owner" | "trainer" | "member";
  name: string;
  verifiedAt?: string | null;
  roles?: Role[];
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  isLoggedIn: boolean;
  hasValidatedSession: boolean;
  isVerified: boolean;
  userData: UserDetailDTO | null;
  hasHydrated: boolean;
  requiresMfaSetup: boolean;
  getWorkspaceAccess: () => WorkspaceAccess;
  canAccessOwnerWorkspace: () => boolean;
  canAccessSuperadminWorkspace: () => boolean;
  setAuthState: (state: Partial<AuthState>) => void;
  setTokens: (access: string, refresh: string) => void;
  setUserData: (data: UserDetailDTO | null | undefined) => void;
  setValidatedSession: (validated: boolean) => void;
  setRequiresMfaSetup: (requires: boolean) => void;
  clearAuthState: (reason?: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      isLoggedIn: false,
      hasValidatedSession: false,
      isVerified: false,
      userData: null,
      hasHydrated: false,
      requiresMfaSetup: false,
      getWorkspaceAccess: () => getUserWorkspaceAccess(get().userData),
      canAccessOwnerWorkspace: () => get().getWorkspaceAccess().canAccessOwnerWorkspace,
      canAccessSuperadminWorkspace: () =>
        get().getWorkspaceAccess().canAccessSuperadminWorkspace,
      setAuthState: (state) => set(state),
      setTokens: (access, refresh) => {
        setAuthCookie(true);
        set({
          accessToken: access,
          refreshToken: refresh,
          isLoggedIn: true,
          hasValidatedSession: false,
        });
      },
      setUserData: (data) => {
        identifyUser(data?.id);
        set({
          userData: data ? normalizeUserWorkspaceAccess(data) : null,
          hasValidatedSession: Boolean(data),
        });
      },
      setValidatedSession: (validated) => set({ hasValidatedSession: validated }),
      setRequiresMfaSetup: (requires) => set({ requiresMfaSetup: requires }),
      clearAuthState: (reason = "unknown") => {
        const previous = get();
        const hadCredentials = previous.isLoggedIn || Boolean(previous.accessToken || previous.refreshToken);
        identifyUser(null);
        setAuthCookie(false);
        set({
          accessToken: null,
          refreshToken: null,
          isLoggedIn: false,
          hasValidatedSession: false,
          isVerified: false,
          userData: null,
          requiresMfaSetup: false,
        });
        if (hadCredentials) {
          Sentry.withScope((scope) => {
            scope.setTag("surface", "web");
            scope.setTag("reason", ["logout", "auth-failure", "account-deleted"].includes(reason) ? reason : "unknown");
            Sentry.captureMessage("auth.session.ended", "info");
          });
        }
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => cookieZustandStorage),
      partialize: ({ hasHydrated, hasValidatedSession, ...state }) => state,
      onRehydrateStorage: () => (state) => {
        // Once hydration finishes, mark hydrated = true
        state?.setAuthState({ hasHydrated: true });
      },
    },
  ),
);

export const selectWorkspaceAccess = (state: AuthState): WorkspaceAccess =>
  getUserWorkspaceAccess(state.userData);
import * as Sentry from "@sentry/nextjs";
